import type {
  ChargeDetail,
  ChargeItem,
  ShipmentAllocationBasis,
  ShipmentAllocationInput,
} from "@/types/charge";

const PRECISION = BigInt(1_000_000);

/** Decimal parsing avoids unsafe integer products, including large stock values. */
export function physicalUnits(quantity: number): bigint {
  if (!Number.isFinite(quantity) || quantity < 0)
    throw new Error("Physical quantities must be finite and nonnegative");
  const [coefficient, exponent = "0"] = String(quantity)
    .toLowerCase()
    .split("e");
  const [whole, fraction = ""] = coefficient.split(".");
  const digits = BigInt(whole + fraction);
  const shift = 6 + Number(exponent) - fraction.length;
  if (shift >= 0) return digits * BigInt(10) ** BigInt(shift);
  const divisor = BigInt(10) ** BigInt(-shift);
  return (digits + divisor / BigInt(2)) / divisor;
}

/** Resize at the mutation boundary even when the inventory editor is closed. */
export function resizePendingDosePlans(
  previous: ChargeDetail,
  next: ChargeDetail,
  savedLineIds: Set<string>,
): ChargeDetail {
  if (previous.id !== next.id) return next; // Hydration/charge switching is not an edit.
  let changed = false;
  const items = next.items.map((line, index) => {
    if (
      line.doseComponentIds?.length !== 1 ||
      line.absorbedFromChargeItemId ||
      (line.id && savedLineIds.has(line.id))
    )
      return line;
    const old = line.id
      ? previous.items.find((entry) => entry.id === line.id)
      : previous.items[index];
    if (!old || old.itemId !== line.itemId) return line;
    const before = old.quantity - (old.savedQuantity ?? 0);
    const after = line.quantity - (line.savedQuantity ?? 0);
    const contextChanged =
      line.soldByProviderId !== old.soldByProviderId ||
      next.locationId !== previous.locationId;
    if (before === after && !contextChanged) return line;
    let allocations = line.shipmentAllocations ?? [];
    for (const basis of line.shipmentAllocationBasis ?? []) {
      if (!basis.originalBilledQuantity || !basis.originalUncappedTarget)
        continue;
      const uncapped =
        (Math.max(0, after) * basis.originalUncappedTarget) /
        basis.originalBilledQuantity;
      const target = Math.min(uncapped, basis.remainingTarget ?? Infinity);
      const ids = new Set(basis.allocations.map((row) => row.shipmentId));
      let plan;
      try {
        plan = allocationFromBasis(
          basis,
          target,
          allocations.filter((row) => ids.has(row.shipmentId)),
        );
      } catch {
        continue;
      }
      if (contextChanged || before !== after)
        plan = plan.map(({ shipmentId, quantity }) => ({
          shipmentId,
          quantity,
        }));
      allocations = [
        ...allocations.filter((row) => !ids.has(row.shipmentId)),
        ...plan,
      ];
    }
    if (
      JSON.stringify(allocations) ===
      JSON.stringify(line.shipmentAllocations ?? [])
    )
      return line;
    changed = true;
    const plannedIds = new Set(
      (line.shipmentAllocationBasis ?? []).flatMap((basis) =>
        basis.allocations.map((row) => row.shipmentId),
      ),
    );
    return {
      ...line,
      shipmentAllocations: allocations,
      shipmentIds: [
        ...(line.shipmentIds ?? []).filter((id) => !plannedIds.has(id)),
        ...allocations
          .filter((row) => plannedIds.has(row.shipmentId))
          .map((row) => row.shipmentId),
      ],
    } satisfies ChargeItem;
  });
  return changed ? { ...next, items } : next;
}

function fromPhysicalUnits(quantity: bigint): number {
  return Number(
    `${quantity / PRECISION}.${String(quantity % PRECISION).padStart(6, "0")}`,
  );
}

/** Mirrors dose/allocation.py: conserved micro-units, largest remainder, receipt-ID tie. */
export function proportionalAllocation(
  allocations: { shipmentId: string; quantity: number }[],
  originalTarget: number,
  target: number,
): ShipmentAllocationInput[] {
  const original = physicalUnits(originalTarget);
  const total = physicalUnits(target);
  const baseline = allocations.map((allocation) => ({
    ...allocation,
    units: physicalUnits(allocation.quantity),
  }));
  if (
    original <= BigInt(0) ||
    !baseline.length ||
    new Set(baseline.map((row) => row.shipmentId)).size !== baseline.length ||
    baseline.some((row) => row.units <= BigInt(0)) ||
    baseline.reduce((sum, row) => sum + row.units, BigInt(0)) !== original
  )
    throw new Error(
      "A proportional allocation requires a complete positive baseline",
    );
  const rows = baseline.map((row) => ({
    shipmentId: row.shipmentId,
    units: (row.units * total) / original,
    remainder: (row.units * total) % original,
  }));
  const residual =
    total - rows.reduce((sum, row) => sum + row.units, BigInt(0));
  const ordered = [...rows].sort((a, b) =>
    a.remainder === b.remainder
      ? a.shipmentId < b.shipmentId
        ? -1
        : a.shipmentId > b.shipmentId
          ? 1
          : 0
      : a.remainder > b.remainder
        ? -1
        : 1,
  );
  for (let index = 0; index < Number(residual); index++)
    ordered[index].units += BigInt(1);
  return rows
    .filter((row) => row.units > BigInt(0))
    .map((row) => ({
      shipmentId: row.shipmentId,
      quantity: fromPhysicalUnits(row.units),
    }));
}

/** Incomplete manual plans never become a baseline. Consent is not a share. */
export function completeAllocationBasis(
  stockId: string,
  unit: ShipmentAllocationBasis["unit"],
  target: number,
  allocations: ShipmentAllocationInput[],
): ShipmentAllocationBasis | undefined {
  try {
    proportionalAllocation(allocations, target, target);
    return {
      stockId,
      unit,
      originalTarget: target,
      allocations: allocations.map(({ shipmentId, quantity }) => ({
        shipmentId,
        quantity,
      })),
    };
  } catch {
    return undefined;
  }
}

/** Exact original shares are kept across edits; changed targets revoke yield consent. */
export function allocationFromBasis(
  basis: ShipmentAllocationBasis,
  target: number,
  current: ShipmentAllocationInput[],
): ShipmentAllocationInput[] {
  const scaled = proportionalAllocation(
    basis.allocations,
    basis.originalTarget,
    target,
  );
  const unchanged =
    scaled.length === current.length &&
    scaled.every((row) =>
      current.some(
        (entry) =>
          entry.shipmentId === row.shipmentId &&
          physicalUnits(entry.quantity) === physicalUnits(row.quantity),
      ),
    );
  return unchanged ? current : scaled;
}
